import React, { useState, useRef, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Sparkles, Send, Loader2, Trash2, User, Bot, ChevronRight } from 'lucide-react';
import { NotesMarkdown } from './NotesMarkdown';

const DEAL_EXAMPLES = [
  'Wann war das letzte Meeting mit dem Inhaber?',
  'Hat der Inhaber etwas über Wettbewerber erwähnt?',
  'Fasse alle bisherigen Gespräche kurz zusammen',
  'Was sind die größten Risiken bei diesem Deal?',
];

const GLOBAL_EXAMPLES = [
  'Welche Deals hatten diese Woche Owner-Kontakt?',
  'Bei welchen Deals warten wir seit über 2 Wochen auf eine Antwort?',
  'Fasse die Aktivitäten der letzten 7 Tage zusammen',
  'Welche Deals stehen kurz vor dem NDA?',
];

// AC members are blocked unless the admin toggle (ac_ai_enabled) is on.
function useAiAccess() {
  const { isAcMember } = useAuth();
  const [enabled, setEnabled] = useState(!isAcMember);
  useEffect(() => {
    if (!isAcMember) { setEnabled(true); return; }
    api.getAiUsage().then(r => setEnabled(r.data.enabled !== false)).catch(() => setEnabled(false));
  }, [isAcMember]);
  return enabled;
}

function AiChatDialog({ open, onOpenChange, title, subtitle, intro, examples, fns, thinkingText }) {
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [usage, setUsage] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    fns.getHistory()
      .then(r => setMessages(r.data.messages || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded, fns]);

  useEffect(() => {
    if (!open || asking) return;
    api.getAiUsage().then(r => setUsage(r.data)).catch(() => {});
  }, [open, asking]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, asking]);

  const ask = async (q) => {
    const text = (q || question).trim();
    if (!text || asking) return;
    setQuestion('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setAsking(true);
    try {
      const r = await fns.ask(text);
      setMessages(prev => [...prev, { role: 'assistant', text: r.data.answer }]);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'AI request failed');
      setMessages(prev => prev.slice(0, -1));
      setQuestion(text);
    }
    setAsking(false);
  };

  const clear = async () => {
    try { await fns.clearHistory(); setMessages([]); } catch { /* ignore */ }
  };

  const limitReached = usage && usage.limit > 0 && usage.remaining === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl p-0 gap-0 flex flex-col max-h-[85vh]" data-testid="ask-ai-dialog">
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border shrink-0">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" /> {title}
              {subtitle && <span className="text-xs text-muted-foreground font-sans font-normal truncate max-w-[220px]">· {subtitle}</span>}
            </DialogTitle>
            {messages.length > 0 && (
              <button onClick={clear} data-testid="ask-ai-clear-btn"
                className="text-muted-foreground hover:text-red-400 transition-colors" title="Clear conversation">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[280px]">
          {messages.length === 0 && !asking && (
            <div className="py-4">
              <p className="text-xs text-muted-foreground mb-3">{intro}</p>
              <div className="space-y-1.5">
                {examples.map((ex, i) => (
                  <button key={i} onClick={() => ask(ex)} data-testid={`ask-ai-example-${i}`}
                    className="w-full text-left text-xs text-violet-300/90 bg-violet-950/20 border border-violet-800/30 hover:border-violet-600/50 rounded-sm px-3 py-2 transition-colors">
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} data-testid={`ask-ai-message-${i}`}
              className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}>
              {m.role !== 'user' && <Sparkles className="w-3.5 h-3.5 text-violet-400 mt-1.5 shrink-0" />}
              <div className={`max-w-[88%] rounded-sm px-3.5 py-2.5 text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-blue-600/20 border border-blue-600/30 text-foreground'
                  : 'bg-secondary border border-border text-foreground'}`}>
                {m.role === 'user' ? <span className="whitespace-pre-wrap">{m.text}</span> : <NotesMarkdown text={m.text} />}
              </div>
              {m.role === 'user' && <User className="w-3.5 h-3.5 text-blue-400 mt-1.5 shrink-0" />}
            </div>
          ))}
          {asking && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="ask-ai-thinking">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400" /> {thinkingText}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="px-4 py-3 border-t border-border shrink-0">
          <div className="flex gap-2">
            <textarea value={question} onChange={e => setQuestion(e.target.value)} rows={1}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder="Frage stellen… (Enter zum Senden)"
              data-testid="ask-ai-input"
              className="flex-1 bg-secondary/50 border border-border rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-violet-500/50 resize-none" />
            <Button onClick={() => ask()} disabled={asking || !question.trim()} data-testid="ask-ai-send-btn"
              className="bg-violet-700 hover:bg-violet-600 text-white rounded-sm h-auto px-3">
              {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2 mt-1.5 text-[10px] text-muted-foreground font-mono" data-testid="ask-ai-usage">
            <span className="truncate">
              {usage ? `Modell: ${usage.model_label} · ` : ''}Anfragen werden pro Nutzer protokolliert
            </span>
            {usage && usage.limit > 0 && (
              <span className={`shrink-0 ${limitReached ? 'text-red-400' : ''}`}>
                {usage.used}/{usage.limit} Anfragen heute{limitReached ? ' — Tageslimit erreicht' : ''}
              </span>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AskOltaAI({ projectId, dealName }) {
  const [open, setOpen] = useState(false);
  const aiEnabled = useAiAccess();
  if (!aiEnabled) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="ask-ai-open-btn"
        className="w-full flex items-center gap-2.5 bg-gradient-to-r from-violet-950/40 via-card to-card border border-violet-700/30 hover:border-violet-500/50 rounded-sm px-4 py-2.5 transition-colors group">
        <Sparkles className="w-4 h-4 text-violet-400 group-hover:text-violet-300" />
        <span className="text-sm text-foreground/90 font-medium">Ask OLTA AI</span>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          — Fragen zu diesem Deal: Meetings, E-Mails, Notizen, Dokumente…
        </span>
      </button>
      <AiChatDialog open={open} onOpenChange={setOpen}
        title="Ask OLTA AI" subtitle={dealName}
        intro="Stelle Fragen zu allem, was auf dieser Deal-Seite geloggt ist — Meetings & Calls, E-Mails, Kommentare, Dokumente, Scorecards, Stage-Historie."
        examples={DEAL_EXAMPLES}
        thinkingText="OLTA AI durchsucht den Deal…"
        fns={{
          ask: (q) => api.askDealAi(projectId, q),
          getHistory: () => api.getAskAiHistory(projectId),
          clearHistory: () => api.clearAskAiHistory(projectId),
        }} />
    </>
  );
}

export function AskOltaAIGlobal() {
  const [open, setOpen] = useState(false);
  const aiEnabled = useAiAccess();
  if (!aiEnabled) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="global-ask-ai-open-btn"
        className="w-full flex items-center gap-3 bg-gradient-to-r from-violet-950/50 via-indigo-950/25 to-card border border-violet-600/40 hover:border-violet-400/60 rounded-sm px-4 py-3 transition-colors group">
        <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-violet-600/20 border border-violet-500/40 shrink-0 group-hover:scale-105 transition-transform">
          <Bot className="w-5 h-5 text-violet-300" />
          <Sparkles className="w-3 h-3 text-amber-300 absolute -top-0.5 -right-0.5" />
        </span>
        <span className="text-left min-w-0 flex-1">
          <span className="block text-sm text-foreground font-medium">Ask OLTA AI</span>
          <span className="block text-xs text-muted-foreground truncate">
            Fragen über die gesamte Pipeline — Deals, Meetings & Calls, Aktivitäten, E-Mails…
          </span>
        </span>
        <ChevronRight className="w-4 h-4 text-violet-400/70 shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </button>
      <AiChatDialog open={open} onOpenChange={setOpen}
        title="Ask OLTA AI" subtitle="alle Deals"
        intro="Stelle Fragen über die gesamte Pipeline — aktive Deals, Meetings & Calls, Stage-Wechsel, Aktivitäten und eingehende E-Mails."
        examples={GLOBAL_EXAMPLES}
        thinkingText="OLTA AI durchsucht die Pipeline…"
        fns={{
          ask: (q) => api.askGlobalAi(q),
          getHistory: () => api.getGlobalAskAiHistory(),
          clearHistory: () => api.clearGlobalAskAiHistory(),
        }} />
    </>
  );
}
